/** @jsx jsx */
// eslint-disable-next-line no-unused-vars
import { jsx } from '@emotion/core';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
// eslint-disable-next-line no-unused-vars
import { Field, Form } from 'react-final-form';
import { dlsColorModerate, IconInfo } from '@americanexpress/dls-react'; // eslint-disable-line no-unused-vars
import {
  callFxgApi,
  FxMultiButtons, // eslint-disable-line no-unused-vars
  FxTextLink, // eslint-disable-line no-unused-vars
  DisplayWarningMessage, // eslint-disable-line no-unused-vars
  Loader, // eslint-disable-line no-unused-vars
  useValidationSchema,
} from 'axp-fx-common';
import { apiPaths } from '../../../constants/paths';
import { registerFormData } from '../registerFormInitData';
import labels from '../../../constants/labels';
import { steps } from '../../../constants/registration';
import { credentialsValidationSchema } from '../../../utils/registrationSchemaUtil';
import { API_SUCCESS_CD, USER_PROFILE_ALREADY_EXISTS_CD } from '../../../constants/apiCodes';
import { INTERNAL_SERVER_ERR_MSG } from '../../../constants/constants';
import fields from '../../../constants/fields';

const Credentials = ({
  accountData,
  credentialsData,
  // setCredentialsData,
  setIsRegistrationSuccess,
  setRegistrationStep,
  regId,
  rsvpCd,
}) => {
  const fxgApiHost = useSelector((state) => state.getIn(['config', 'fxgApi']));
  const [credentialsErrorData, setCredentialsErrorData] = useState([]);
  const [showPageBlockLoader, setPageBlockLoader] = useState(false);

  const handleSubmitButtonClick = async (formValues) => {
    setCredentialsErrorData([]);
    setPageBlockLoader(true);
    const reqData = {
      ...formValues,
      ...accountData,
      regId,
      rsvpCd,
    };

    const {
      response,
      hasErrors,
    } = await callFxgApi(
      `${fxgApiHost}${apiPaths.signUp}`,
      'POST',
      reqData
    );
    setPageBlockLoader(false);
    if (!hasErrors && response?.respCd === API_SUCCESS_CD) {
      setIsRegistrationSuccess(true);
    } else if (response?.errorCd === USER_PROFILE_ALREADY_EXISTS_CD) {
      return {
        userIdentifier:
          'User ID entered is already exist, please select/enter another',
      };
    } else {
      setCredentialsErrorData([INTERNAL_SERVER_ERR_MSG]);
    }
    return null;
  };

  // eslint-disable-next-line no-unused-vars -- revisit after how to handle Form previous button click
  const handlePreviousButtonClick = (_formValues) => {
    // setCredentialsData(formValues);
    setRegistrationStep(steps.ACCOUNT);
  };

  const isFormInvalid = (form) => {
    const fieldsWithErrors = Object.keys(form.getState()?.errors);
    return fieldsWithErrors?.length > 0;
  };

  const handleOnSecurityPinChange = (event, form) => {
    const securityPinRegex = /^[\d\b]+$/; // match only numbers
    if (
      event.target.value === ''
      || securityPinRegex.test(event.target.value)
    ) {
      form.change(fields.securityPin, event.target.value);
    }
  };

  const validate = useValidationSchema(credentialsValidationSchema);

  return (
    <Form
      validate={validate}
      onSubmit={(formValues) => handleSubmitButtonClick(formValues)}
      debug={(state, fieldStates) => console.log(state, fieldStates)}
      initialValues={credentialsData}
      render={({
        handleSubmit,
        form,
        values,
      }) => (
        <form onSubmit={handleSubmit}>
          <Loader showLoader={showPageBlockLoader} />
          {credentialsErrorData.length > 0 && (
            <DisplayWarningMessage messageDataList={credentialsErrorData} />
          )}
          <div className="flex">
            <Field {...registerFormData.userIdentifier} />
          </div>
          <div className="flex margin-2-t">
            <Field {...registerFormData.password} />
            <Field {...registerFormData.confirmPassword} />
          </div>

          <div className="alert alert-warn margin-2-t pad-1" role="alert">
            <div className="flex margin-1-t dls-color-moderate">
              <IconInfo
                title="Contact Info"
                titleId="contact-info"
                size="md"
                filled={true}
                theme={dlsColorModerate}
              />
              <p className="margin-1-l">
                Please record your security code. The security code is required to recover a forgotten username
                or password. Without the security code, your profile will need to be re-established and the
                registration process will need to be restarted which will cause delays.
              </p>
            </div>
          </div>
          <div className="flex margin-2-t">
            <Field
              {...registerFormData.securityPin}
              handleOnChange={(event) => handleOnSecurityPinChange(event, form)}
            />
          </div>
          <div className="flex margin-2-t">
            <Field {...registerFormData.auth} showError={false} />
            <div
              className="col-md-11"
              css={{
                marginLeft: '-20px',
                marginTop: '8px',
              }}
            >
              <p className="body-3 dls-gray-06">
                By providing your e-mail address, you agree to receive information
                about FileXpress and other products, services and offers from
                American Express. For information about how we protect your
                privacy, please read our{' '}
                <FxTextLink
                  text="Privacy Statement"
                  onTextClick={() => window.open('https://www.americanexpress.com/us/company/privacy-center/online-privacy-disclosures/', '_blank')}
                />
                .
              </p>
            </div>
          </div>

          <div className="margin-4-t">
            <FxMultiButtons
              primaryButtonLabel={labels.SUBMIT}
              isDisablePrimaryButton={isFormInvalid(form)}
              secondaryButtonLabel={labels.PREVIOUS}
              onSecondaryButtonClick={() => handlePreviousButtonClick(values)}
              justifyButtons="end"
            />
          </div>
        </form>
      )}
    />
  );
};

Credentials.propTypes = {
  accountData: PropTypes.shape({}).isRequired,
  credentialsData: PropTypes.shape({}).isRequired,
  setCredentialsData: PropTypes.func.isRequired,
  setIsRegistrationSuccess: PropTypes.func.isRequired,
  setRegistrationStep: PropTypes.func.isRequired,
  regId: PropTypes.string.isRequired,
  rsvpCd: PropTypes.string.isRequired,
};

export default Credentials;
